import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import supabase from '../services/supabase';
import { usageService } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasMadeTopUp, setHasMadeTopUp] = useState(false);
  // Track the last time we verified the backend user
  const lastUserVerification = useRef(0);
  // Track if a verification is in progress
  const verificationInProgress = useRef(false);

  // Login with email and password
  const loginWithEmail = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);
        return { success: false, error };
      }

      if (data?.session) {
        // User is authenticated
        await processSession(data.session, data.user, true); // Force verification on login
        return { success: true };
      }
      
      return { success: false, error: { message: 'Authentication failed' } };
    } catch (error) {
      console.error('Email authentication failed:', error);
      return { success: false, error };
    }
  };

  // Register with email and password
  const registerWithEmail = async (email, password, name) => {
    try {
      // Register the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: name
          }
        }
      });

      if (error) {
        console.error('Registration error:', error);
        return { success: false, error };
      }

      return { 
        success: true, 
        needsEmailConfirmation: !data.user?.email_confirmed_at
      };
    } catch (error) {
      console.error('Email registration failed:', error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error('Password reset error:', error);
        return { success: false, error };
      }

      return { success: true };
    } catch (error) {
      console.error('Password reset failed:', error);
      return { success: false, error };
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/dashboard'
        }
      });

      if (error) {
        console.error('Google OAuth error:', error);
        return false;
      }
      
      // This only initiates the OAuth flow, user will be redirected to Google
      return true;
    } catch (error) {
      console.error('Google authentication failed:', error);
      throw error;
    }
  };

  // Helper function to process session data and update state
  const processSession = async (session, user, forceVerification = false) => {
    if (!session || !user) return false;

    console.log('Processing session for user:', user);
    
    const { access_token, refresh_token } = session;
    
    // Store tokens for API authentication
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    localStorage.setItem('apiKey', access_token); // For API compatibility 
    
    // Extract user metadata
    const name = user.user_metadata?.name || user.email;
    const picture = user.user_metadata?.avatar_url || user.user_metadata?.picture;
    
    // Create user object
    const userData = {
      id: user.id,
      email: user.email,
      name,
      picture,
      apiKey: access_token, // For API compatibility
      accessToken: access_token,
      refreshToken: refresh_token
    };
    
    // Update state
    setCurrentUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Check if user has made a top-up - immediate check only for sign-in/sign-up
    // For normal page loads, this will run in the background
    const isSignInOrSignUp = forceVerification;
    
    if (isSignInOrSignUp) {
      // For sign-in/sign-up: block and wait for the result
      try {
        const userProfile = await usageService.getUserProfile();
        setHasMadeTopUp(userProfile.has_made_top_up || false);
        console.log('User has made top-up (immediate check):', userProfile.has_made_top_up);
      } catch (error) {
        console.error('Error checking top-up status:', error);
        setHasMadeTopUp(false);
      }
    } else {
      // For normal page loads: run in background
      // This allows the page to load quickly without waiting for this check
      setTimeout(async () => {
        try {
          const userProfile = await usageService.getUserProfile();
          setHasMadeTopUp(userProfile.has_made_top_up || false);
          console.log('User has made top-up (background check):', userProfile.has_made_top_up);
        } catch (error) {
          console.error('Error checking top-up status in background:', error);
          setHasMadeTopUp(false);
        }
      }, 0);
    }
    
    // After successful Supabase auth, call backend to ensure DB records exist
    // But only if it's been more than 1 hour since the last verification or on login/force
    const now = Date.now();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    const shouldVerify = forceVerification || 
                        (now - lastUserVerification.current > oneHour) || 
                        !lastUserVerification.current;
    
    if (shouldVerify && !verificationInProgress.current) {
      verificationInProgress.current = true;
      
      try {
        await ensureBackendUserExists(access_token);
        lastUserVerification.current = now;
      } finally {
        verificationInProgress.current = false;
      }
    }
    
    return true;
  };
  
  // Extracted as a separate function for clarity
  const ensureBackendUserExists = async (token) => {
    try {
      console.log('Ensuring user exists in backend DB...');
      
      // Only use the proxy URL for consistency
      const proxyUrl = '/auth/user';  // Local proxy URL (setupProxy.js)
      
      let success = false;
      let retryCount = 0;
      const maxRetries = 3;
      
      // Try with retries
      while (retryCount < maxRetries) {
        try {
          console.log(`Trying API call to: ${proxyUrl} (attempt ${retryCount + 1})`);
          const response = await fetch(proxyUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'auth': token,
              'X-Api-Key': token
            },
            credentials: 'include',
            mode: 'cors', // Explicitly set CORS mode
            cache: 'no-cache' // Disable caching
          });
          
          if (response.ok) {
            console.log(`Success with call to ${proxyUrl}`);
            const userData = await response.json();
            console.log('User data:', userData);
            success = true;
            break;
          } else {
            console.warn(`Failed call to ${proxyUrl}:`, await response.text());
          }
        } catch (fetchError) {
          console.warn(`Error with call to ${proxyUrl} (attempt ${retryCount + 1}):`, fetchError);
        }
        
        retryCount++;
        if (retryCount < maxRetries) {
          // Add exponential backoff
          const backoffTime = Math.pow(2, retryCount) * 200; // 400ms, 800ms, 1600ms...
          console.log(`Retrying in ${backoffTime}ms...`);
          await new Promise(resolve => setTimeout(resolve, backoffTime));
        }
      }
      
      // Don't hide errors - if API calls fail, we should know about it
      if (!success) {
        console.error('API attempts failed. User may experience authentication issues.');
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error ensuring user exists in backend:', error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Logout from Supabase
      await supabase.auth.signOut();
    } finally {
      // Clear local storage
      localStorage.removeItem('apiKey');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Reset state
      setCurrentUser(null);
      setIsAuthenticated(false);
      // Reset verification tracker
      lastUserVerification.current = 0;
    }
  };

  // Check for stored session on mount
  useEffect(() => {
    const setupAuth = async () => {
      try {
        // Get initial session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          await processSession(session, session.user);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Supabase auth state changed:', event);
        
        if (session) {
          // For auth state changes, don't force verification unless it's a new sign-in
          const forceVerify = event === 'SIGNED_IN';
          await processSession(session, session.user, forceVerify);
        } else if (event === 'SIGNED_OUT') {
          // Clear local storage
          localStorage.removeItem('apiKey');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          
          // Reset state
          setCurrentUser(null);
          setIsAuthenticated(false);
          // Reset verification tracker
          lastUserVerification.current = 0;
        }
      }
    );
    
    // Initialize auth
    setupAuth();
    
    // Cleanup subscription on unmount
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    isAuthenticated,
    loading,
    hasMadeTopUp,
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    resetPassword,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}